import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, useTheme } from "@mui/material";
import { type RefObject } from "react";

import { GroupedFiniteList } from "../../components/GroupedFiniteList";
import { GroupedList } from "../../components/GroupedList";
import { ListLoadingState } from "../../components/ListLoadingState";
import { type UpdateFavoriteWorkplacesAnalytics } from "./types";
import {
  type GroupedItem,
  type ItemGroups,
  type WorkplacesSelectionMode,
} from "./useWorkplacesSelection";
import { WorkplaceItem } from "./WorkplaceItem";

interface WorkplacesSelectionListProps {
  scrollRef: RefObject<HTMLDivElement>;
  maxFavoriteCount: number;
  itemGroups: ItemGroups;
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  analytics: {
    mode: WorkplacesSelectionMode;
  };
}

// We only show active and other workplaces for infinite scroll list
const INFINITE_SCROLL_GROUP_ORDER: Array<GroupedItem["groupId"]> = ["active", "other"];

function renderGroupHeader(groupId: GroupedItem["groupId"], itemGroups: ItemGroups) {
  const groupItem = itemGroups[groupId];
  if (!isDefined(groupItem)) {
    return null;
  }

  return (
    <Stack direction="row" gap={2}>
      <Text variant="body1" color={(theme) => theme.palette.text.tertiary}>
        {groupItem.title}
      </Text>
      {isDefined(groupItem.count) && (
        <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
          {groupItem.count}
        </Text>
      )}
    </Stack>
  );
}

function renderItem(params: {
  item: GroupedItem;
  itemGroups: ItemGroups;
  maxFavoriteCount: number;
  analytics: Pick<UpdateFavoriteWorkplacesAnalytics, "mode">;
}) {
  const { item, itemGroups, maxFavoriteCount, analytics } = params;

  if (item.type !== "workplace") {
    return null;
  }

  return (
    <WorkplaceItem
      key={item.workplaceId}
      item={item}
      isFavorite={isDefined(item.favoriteId)}
      favoriteId={item.favoriteId}
      favoriteWorkplacesCount={itemGroups.favorite.count ?? 0}
      maxFavoriteCount={maxFavoriteCount}
      data-testid={`workplace-item-${item.groupId}-${item.workplaceId}`}
      analytics={{ mode: analytics.mode, group: item.groupId }}
    />
  );
}

export function WorkplacesSelectionList(props: WorkplacesSelectionListProps) {
  const {
    scrollRef,
    maxFavoriteCount,
    itemGroups,
    isLoading,
    isError,
    isFetchingNextPage,
    onLoadMore,
    analytics,
  } = props;

  const theme = useTheme();

  if (isError) {
    return <Text align="center">Error loading workplaces. Please try again.</Text>;
  }

  return (
    <>
      {/**
       * Keep favorite workplaces always visible on top for infinite scroll list.
       * The number of favorite workplaces shouldn't exceed 2-3 items. If this changes in future, we will need to change this.
       */}
      <GroupedFiniteList
        items={itemGroups.favorite.items}
        renderItem={(item) => renderItem({ item, itemGroups, maxFavoriteCount, analytics })}
        groupBy={(item) => item.groupId}
        // @ts-expect-error - Type of `groupId` is not strict enough in the GroupedList component
        renderGroupHeader={(groupId: GroupedItem["groupId"]) =>
          renderGroupHeader(groupId, itemGroups)
        }
      />
      <GroupedList
        customScrollParentRef={scrollRef}
        items={[...itemGroups.active.items, ...itemGroups.other.items]}
        isLoadingMore={isFetchingNextPage}
        groupBy={(item) => item.groupId}
        backgroundColor={theme.palette.common.white}
        // @ts-expect-error - Type of `groupId` is not strict enough in the GroupedList component
        sortGroupKeys={(keyA: GroupedItem["groupId"], keyB: GroupedItem["groupId"]) =>
          INFINITE_SCROLL_GROUP_ORDER.indexOf(keyA) - INFINITE_SCROLL_GROUP_ORDER.indexOf(keyB)
        }
        // @ts-expect-error - Type of `groupId` is not strict enough in the GroupedList component
        renderGroupHeader={(groupId: GroupedItem["groupId"]) =>
          renderGroupHeader(groupId, itemGroups)
        }
        renderItem={(item) => renderItem({ item, itemGroups, maxFavoriteCount, analytics })}
        endReached={() => {
          onLoadMore();
        }}
        scrollerTestId="workplaces-selection-list-scroll-container"
        contentPlaceholder={
          <ListLoadingState
            texts={[
              "Finding workplaces nearby",
              "Analyzing workplace schedules",
              "Workplaces are on the way",
            ]}
          />
        }
        showPlaceholder={isLoading}
      />
    </>
  );
}
